import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { esTardio } from 'lib/tardio';
import { Link } from 'react-router-dom';
import {
  AlertOctagon, Ban, CalendarCheck2, CalendarClock, CalendarX2, ChevronRight, CircleHelp, FileCheck2, ListChecks, Plus,
  Hourglass, ShieldCheck, Spline, TreeDeciduous,
} from 'lucide-react';
import { fetchInspeccionesPeriodo, fetchDiasSinOperacion, fetchReportes } from 'services/adminService';
import { fetchArbolesEstado } from 'services/arbolesService';
import { fetchLineasEstado } from 'services/cablesService';
import { normalizarFilaEstado as normalizarLinea, resumenLineas } from 'lib/cables';
import {
  coberturaPorDia, resumenCobertura, sumarDias, fechaCorta, fechaHoraCorta, horaCR, nombreUsuario,
  plural, AVISO_SIN_OPERACION,
} from 'lib/auditoria';
import { normalizarFilaEstado, resumenArboles } from 'lib/arboles';
import useNombres from 'components/useNombres';
import Kpi from 'components/Kpi';
import {
  INSTALACIONES, filtrarPorInstalacion, checklistDe, checklistVigente, TEXTO_CHECKLIST_PENDIENTE,
  etiquetaInstalacion,
} from 'lib/auditoriaInstalaciones';
import { flattenHallazgos } from 'lib/hallazgos';
import { hoyCR } from 'lib/fechaCR';
import { ESTADO_SOLIDO, ESTADO_LABEL, ESTADO_FILA } from 'lib/estadoUi';
import { rutaInspeccion } from 'lib/auditoriaInspeccion';
import { useAuth } from 'contexts/AuthContext';
import AppShell, { puedeInspeccionar } from 'components/AppShell';
import EstadoPill from 'components/EstadoPill';
import Cargando from 'components/Cargando';
import Button from 'components/ui/Button';
import Modal from 'components/ui/Modal';
import ErrorState from 'components/ui/ErrorState';
import EmptyState from 'components/ui/EmptyState';
import { mensajeError } from 'lib/errores';

const DIAS = 30;
const MAX_HALLAZGOS = 6;

const conInst = (ruta, inst) => (inst === 'canopy' ? ruta : `${ruta}?instalacion=${inst}`);
export const rutaNuevaInspeccion = (inst) => conInst('/inspeccion', inst);

// Clases de cada día en la tira de cobertura (tokens de VLS y estadoUi).
const CLASE_DIA = {
  sin_operacion: 'bg-muted border-border',
  sin_registro: 'bg-white border-2 border-warning',
  pendiente: 'bg-white border-dashed border-muted-foreground/50',
};
const TEXTO_DIA = {
  inspeccionado: 'Inspeccionado',
  sin_operacion: 'No se operó',
  sin_registro: 'Sin inspección',
  pendiente: 'Hoy, pendiente',
};

function TiraCobertura({ dias }) {
  return (
    <ol className="flex flex-wrap gap-1" aria-label={`Inspecciones de los últimos ${DIAS} días`}>
      {dias.map((d) => {
        const clase = d.estado === 'inspeccionado'
          ? ESTADO_SOLIDO[d.resultado] || 'bg-success border-success'
          : CLASE_DIA[d.estado];
        const texto = `${fechaCorta(d.fecha)}: ${TEXTO_DIA[d.estado]}${d.resultado ? `, ${ESTADO_LABEL[d.resultado]}` : ''}`;
        const cuadro = <span className={`block h-5 w-5 rounded border sm:h-6 sm:w-6 ${clase}`} />;
        return (
          <li key={d.fecha} title={texto} aria-label={texto} data-testid={`cuadro-${d.fecha}`} data-estado={d.estado}>
            {d.inspecciones.length === 1
              ? <Link to={rutaInspeccion(d.inspecciones[0].id)} aria-label={texto}>{cuadro}</Link>
              : cuadro}
          </li>
        );
      })}
    </ol>
  );
}

function Leyenda() {
  const item = (clase, texto) => (
    <span className="inline-flex items-center gap-1"><span className={`inline-block h-3 w-3 rounded-sm border ${clase}`} />{texto}</span>
  );
  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
      {item(ESTADO_SOLIDO.aprobado, 'Aprobado')}
      {item(ESTADO_SOLIDO.alerta, 'Alerta')}
      {item(ESTADO_SOLIDO.falla, 'Falla')}
      {item(CLASE_DIA.sin_operacion, 'No se operó')}
      {item(CLASE_DIA.sin_registro, 'Sin inspección')}
    </div>
  );
}

function Hoy({ dia, vigente }) {
  if (!dia) return null;
  if (dia.estado === 'inspeccionado') {
    return (
      <>
        <div className="flex flex-wrap items-center gap-2">
          <CalendarCheck2 size={18} className="text-success" aria-hidden="true" />
          <span className="font-bold text-foreground">Inspeccionado</span>
          <EstadoPill estado={dia.resultado} />
        </div>
        <ul className="mt-1 space-y-0.5 text-sm text-muted-foreground">
          {dia.inspecciones.map((r) => (
            <li key={r.id}>
              <Link to={rutaInspeccion(r.id)} className="underline-offset-2 hover:underline">
                {r.guia_nombre || 'Sin nombre'} · {horaCR(r.created_at) || horaCR(r.fecha_hora)}{esTardio(r) ? ' · tardío' : ''}
              </Link>
            </li>
          ))}
        </ul>
      </>
    );
  }
  if (dia.estado === 'sin_operacion') {
    return (
      <>
        <div className="flex items-center gap-2">
          <Ban size={18} className="text-muted-foreground" aria-hidden="true" />
          <span className="font-bold text-foreground">No se operó</span>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{dia.sinOperacion?.motivo}</p>
      </>
    );
  }
  return (
    <>
      <div className="flex items-center gap-2">
        <CalendarX2 size={18} className={vigente ? 'text-warning-foreground' : 'text-muted-foreground'} aria-hidden="true" />
        <span className="font-bold text-foreground">Sin inspección hoy</span>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        {vigente ? 'Todavía no hay una inspección registrada para hoy.' : 'El checklist todavía se está validando; no se registran inspecciones.'}
      </p>
    </>
  );
}

function TarjetaInstalacion({ inst, datos, hoy, nueva, onVerChecklist }) {
  const lista = checklistDe(inst.id);
  const vigente = checklistVigente(inst.id);
  const dias = useMemo(() => coberturaPorDia({
    inspecciones: datos.inspecciones, diasSinOperacion: datos.sinOp || [], desde: sumarDias(hoy, -(DIAS - 1)), hasta: hoy, hoy,
  }), [datos, hoy]);
  const r = resumenCobertura(dias);
  const diaHoy = dias[dias.length - 1];
  const peor = diaHoy?.estado === 'inspeccionado' ? diaHoy.resultado : null;
  const tono = peor ? {
    falla: 'border-error/50', alerta: 'border-warning/60', aprobado: 'border-success/40',
  }[peor] : (diaHoy?.estado === 'pendiente' && vigente ? 'border-warning/60' : 'border-border');

  return (
    <section aria-labelledby={`t-${inst.id}`} data-testid={`instalacion-${inst.id}`}
      className={`flex flex-col rounded-xl border bg-card p-4 ${tono}`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 id={`t-${inst.id}`} className="text-lg font-bold text-foreground">{inst.label}</h2>
        {lista?.pendiente && (
          <span className="rounded-full border border-warning/50 bg-warning/10 px-2.5 py-0.5 text-xs font-semibold text-warning-foreground">
            {TEXTO_CHECKLIST_PENDIENTE}
          </span>
        )}
      </div>

      <div className={`rounded-xl p-3 ${peor ? ESTADO_FILA[peor] || 'bg-muted/40' : 'bg-muted/40'}`}>
        <div className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">Hoy · {fechaCorta(hoy)}</div>
        <Hoy dia={diaHoy} vigente={vigente} />
        {!datos.sinOp && (
          <p className="mt-1 text-xs text-muted-foreground">No se pudo consultar si hoy se marcó sin operación.</p>
        )}
      </div>

      <div className="mt-3">
        <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">Últimos {DIAS} días</div>
        {lista?.pendiente ? (
          // Sin checklist vigente no hay obligación de inspeccionar: los días
          // no se cuentan como "sin inspección" (se leerían como incumplimiento).
          <p className="text-sm text-muted-foreground" data-testid={`cobertura-${inst.id}`}>
            Los días con y sin inspección se cuentan desde que se active el checklist.
          </p>
        ) : (
          <>
            <TiraCobertura dias={dias} />
            <Leyenda />
            <p className="mt-2 text-sm text-foreground" data-testid={`cobertura-${inst.id}`}>
              {datos.sinOp
                ? `${plural(r.conInspeccion, 'día con inspección', 'días con inspección')}, ${plural(r.sinRegistro, 'día sin inspección', 'días sin inspección')}, ${plural(r.sinOperacion, 'día que no se operó', 'días que no se operó')}.`
                : `${plural(r.conInspeccion, 'día con inspección', 'días con inspección')}. ${AVISO_SIN_OPERACION}`}
            </p>
          </>
        )}
      </div>

      <div className="mt-auto flex flex-wrap gap-2 pt-3">
        {nueva && vigente && (
          <Button asChild size="sm"><Link to={rutaNuevaInspeccion(inst.id)}><Plus size={16} className="mr-1.5" aria-hidden="true" />Nueva inspección</Link></Button>
        )}
        {lista?.pendiente && (
          <Button type="button" variant="outline" size="sm" onClick={() => onVerChecklist(inst.id)}>
            <ListChecks size={16} className="mr-1.5" aria-hidden="true" />Ver checklist propuesto
          </Button>
        )}
        <Button asChild variant="outline" size="sm"><Link to={conInst('/inspecciones', inst.id)}>Inspecciones</Link></Button>
        <Button asChild variant="outline" size="sm"><Link to={conInst('/hallazgos', inst.id)}>Hallazgos</Link></Button>
      </div>
    </section>
  );
}

// Cables de las dos instalaciones: activos, con observaciones o no aptos,
// revisión vencida y cambio vencido (vida útil pasada). Carga aparte, como
// los árboles: si la base todavía no tiene la sección, lo dice sin tapar el
// resto del estado.
function TarjetaCables({ hoy }) {
  const [datos, setDatos] = useState({ cargando: true, error: null, filas: [] });
  const cargar = useCallback(async () => {
    setDatos((d) => ({ ...d, cargando: true, error: null }));
    try {
      setDatos({ cargando: false, error: null, filas: await fetchLineasEstado() });
    } catch (e) {
      setDatos({ cargando: false, error: e, filas: [] });
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  const r = useMemo(() => resumenLineas(datos.filas.map((f) => normalizarLinea(f, hoy))), [datos.filas, hoy]);

  return (
    <section aria-labelledby="t-cables" data-testid="tarjeta-cables" className="rounded-xl border border-border bg-card p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 id="t-cables" className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Cables</h2>
        <Link to="/cables" className="inline-flex items-center gap-0.5 text-sm font-medium text-primary hover:underline">
          Ver cables <ChevronRight size={14} aria-hidden="true" />
        </Link>
      </div>
      {datos.error ? (
        <ErrorState size="sm" title="No se pudieron cargar los cables" message={mensajeError(datos.error)} onRetry={cargar} />
      ) : datos.cargando ? (
        <Cargando className="py-4" texto="Consultando cables…" />
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Kpi icon={Spline} label="Cables activos" value={r.activos} />
          <Kpi icon={AlertOctagon} label="Con observaciones o no apto" value={r.observados} tono={r.noApto ? 'falla' : r.observados ? 'alerta' : 'neutral'} />
          <Kpi icon={CalendarClock} label="Revisión vencida" value={r.vencidas} tono={r.vencidas ? 'alerta' : 'neutral'} />
          <Kpi icon={Hourglass} label="Cambio vencido" value={r.cambioVencido} tono={r.cambioVencido ? 'falla' : 'neutral'} />
        </div>
      )}
    </section>
  );
}

// Árboles de las dos instalaciones: activos, riesgo alto o extremo,
// evaluaciones vencidas y sin evaluación. Carga aparte: si la base todavía no
// tiene la sección, lo dice sin tapar el resto del estado.
function TarjetaArboles({ hoy }) {
  const [datos, setDatos] = useState({ cargando: true, error: null, filas: [] });
  const cargar = useCallback(async () => {
    setDatos((d) => ({ ...d, cargando: true, error: null }));
    try {
      setDatos({ cargando: false, error: null, filas: await fetchArbolesEstado() });
    } catch (e) {
      setDatos({ cargando: false, error: e, filas: [] });
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  const r = useMemo(() => resumenArboles(datos.filas.map((f) => normalizarFilaEstado(f, hoy))), [datos.filas, hoy]);

  return (
    <section aria-labelledby="t-arboles" data-testid="tarjeta-arboles" className="rounded-xl border border-border bg-card p-4">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 id="t-arboles" className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Árboles</h2>
        <Link to="/arboles" className="inline-flex items-center gap-0.5 text-sm font-medium text-primary hover:underline">
          Ver árboles <ChevronRight size={14} aria-hidden="true" />
        </Link>
      </div>
      {datos.error ? (
        <ErrorState size="sm" title="No se pudieron cargar los árboles" message={mensajeError(datos.error)} onRetry={cargar} />
      ) : datos.cargando ? (
        <Cargando className="py-4" texto="Consultando árboles…" />
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Kpi icon={TreeDeciduous} label="Árboles activos" value={r.activos} />
          <Kpi icon={AlertOctagon} label="Riesgo alto o extremo" value={r.altoExtremo} tono={r.altoExtremo ? 'falla' : 'neutral'} />
          <Kpi icon={CalendarClock} label="Evaluaciones vencidas" value={r.vencidas} tono={r.vencidas ? 'alerta' : 'neutral'} />
          <Kpi icon={CircleHelp} label="Sin evaluación" value={r.sinEvaluacion} tono={r.sinEvaluacion ? 'alerta' : 'neutral'} />
        </div>
      )}
    </section>
  );
}

function ModalChecklist({ instalacion, onClose }) {
  const lista = instalacion ? checklistDe(instalacion) : null;
  return (
    <Modal open={!!lista} onClose={onClose} title={`Checklist propuesto: ${instalacion ? etiquetaInstalacion(instalacion) : ''}`} maxWidth="max-w-lg"
      footer={<Button type="button" variant="outline" size="lg" className="w-full" onClick={onClose}>Cerrar</Button>}>
      <p className="mb-3 text-sm text-muted-foreground">
        Propuesta en validación con el inspector. Solo lectura: mientras no se apruebe, no se registran inspecciones de esta instalación.
      </p>
      {(lista?.secciones || []).map((s) => (
        <div key={s.id || s.titulo} className="mb-3">
          {s.titulo && <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">{s.titulo}</h3>}
          <ol className="list-decimal space-y-1 pl-5 text-sm text-foreground">
            {(s.items || []).map((it) => (
              <li key={it.key}>
                <span className="font-medium">{it.label}</span>
                {it.descripcion && <span className="block text-xs text-muted-foreground">{it.descripcion}</span>}
              </li>
            ))}
          </ol>
        </div>
      ))}
    </Modal>
  );
}

export default function Estado() {
  const auth = useAuth();
  const nueva = puedeInspeccionar(auth);
  const hoy = hoyCR();
  const desde = sumarDias(hoy, -(DIAS - 1));
  const [datos, setDatos] = useState({ cargando: true, error: null, inspecciones: [], sinOp: null });
  const [ultimo, setUltimo] = useState({ cargando: true, reporte: null, error: null });
  const [verChecklist, setVerChecklist] = useState(null);

  const cargar = useCallback(async () => {
    setDatos((d) => ({ ...d, cargando: true, error: null }));
    try {
      // Sin filtro de instalación: se trae todo y se separa aquí (las filas
      // sin la columna son del canopy).
      const [inspecciones, sinOp] = await Promise.all([
        fetchInspeccionesPeriodo({ desde, hasta: hoy }),
        fetchDiasSinOperacion({ desde, hasta: hoy }).catch(() => null),
      ]);
      setDatos({ cargando: false, error: null, inspecciones, sinOp });
    } catch (e) {
      setDatos({ cargando: false, error: e, inspecciones: [], sinOp: null });
    }
  }, [desde, hoy]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    let vivo = true;
    fetchReportes({ limite: 1 })
      .then((r) => { if (vivo) setUltimo({ cargando: false, reporte: r[0] || null, error: null }); })
      .catch((e) => { if (vivo) setUltimo({ cargando: false, reporte: null, error: e }); });
    return () => { vivo = false; };
  }, []);
  const nombres = useNombres(useMemo(() => [ultimo.reporte?.generado_por], [ultimo.reporte]));

  const hallazgos = useMemo(() => flattenHallazgos(datos.inspecciones), [datos.inspecciones]);
  const recientes = hallazgos.slice(0, MAX_HALLAZGOS);

  return (
    <AppShell titulo="Estado" descripcion={`Cables y cuerdas, hoy ${fechaCorta(hoy)}`}>
      {datos.error ? (
        <ErrorState title="No se pudo cargar el estado" message={mensajeError(datos.error)} onRetry={cargar} />
      ) : datos.cargando ? (
        <Cargando texto="Consultando el estado de hoy…" />
      ) : (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {INSTALACIONES.map((inst) => (
              <TarjetaInstalacion key={inst.id} inst={inst} hoy={hoy} nueva={nueva} onVerChecklist={setVerChecklist}
                datos={{
                  inspecciones: filtrarPorInstalacion(datos.inspecciones, inst.id),
                  sinOp: datos.sinOp ? filtrarPorInstalacion(datos.sinOp, inst.id) : null,
                }} />
            ))}
          </div>

          <section aria-labelledby="t-hallazgos" className="rounded-xl border border-border bg-card p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <h2 id="t-hallazgos" className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
                Hallazgos recientes · últimos {DIAS} días
              </h2>
              <Link to="/hallazgos" className="inline-flex items-center gap-0.5 text-sm font-medium text-primary hover:underline">
                Ver hallazgos <ChevronRight size={14} aria-hidden="true" />
              </Link>
            </div>
            {recientes.length === 0 ? (
              <EmptyState size="sm" icon={<ShieldCheck size={22} />} title="Sin alertas ni fallas en los últimos 30 días." />
            ) : (
              <ul className="divide-y divide-border">
                {recientes.map((h) => (
                  <li key={`${h.inspeccion_id}:${h.itemKey}`} data-testid="hallazgo-reciente">
                    <Link to={rutaInspeccion(h.inspeccion_id)} className="flex flex-wrap items-center gap-x-2 gap-y-1 py-2 hover:bg-muted/40">
                      <EstadoPill estado={h.estado} />
                      <span className="text-sm font-medium text-foreground">{h.item}</span>
                      <span className="text-xs text-muted-foreground">
                        {etiquetaInstalacion(h.instalacion)} · {fechaCorta(h.fecha)} · {h.guia || 'Sin nombre'}
                      </span>
                    </Link>
                  </li>
                ))}
                {hallazgos.length > recientes.length && (
                  <li className="pt-2 text-xs text-muted-foreground">
                    y {plural(hallazgos.length - recientes.length, 'hallazgo más', 'hallazgos más')} en el período.
                  </li>
                )}
              </ul>
            )}
          </section>

          <TarjetaCables hoy={hoy} />
          <TarjetaArboles hoy={hoy} />

          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground" data-testid="ultimo-reporte">
            <FileCheck2 size={14} aria-hidden="true" />
            {ultimo.cargando ? 'Consultando el último reporte…'
              : ultimo.error ? 'No se pudo consultar el último reporte generado.'
                : !ultimo.reporte ? 'Todavía no se ha generado ningún reporte.'
                  : <>Último reporte generado: {fechaHoraCorta(ultimo.reporte.generado_at)}{ultimo.reporte.generado_por ? ` por ${nombreUsuario(ultimo.reporte.generado_por, nombres)}` : ''}.</>}
            <Link to="/reportes" className="inline-flex items-center gap-0.5 font-medium text-primary hover:underline">
              Reportes <ChevronRight size={12} aria-hidden="true" />
            </Link>
          </p>
        </div>
      )}
      <ModalChecklist instalacion={verChecklist} onClose={() => setVerChecklist(null)} />
    </AppShell>
  );
}
